import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useContext, useEffect } from "react";
import { ConfigContext, OrderDataContext, ReturnOrderContext } from "@/Layout";
import { useUpsellProducts } from "@/api/upsellProducts";
import { BoxedImage } from "@/components/BoxedImage/BoxedImage";
import LoadSpinner from "@/components/LoadSpinner/LoadSpinner";
import { ReturnContainer } from "@/components/ReturnContainer/ReturnContainer";
import { cn } from "@/utils/utils";

export const Route = createFileRoute("/upsell-offer")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { returnOrder } = useContext(ReturnOrderContext);
  const {
    data: upsellProducts,
    isError: upsellProductsHasError,
    isLoading: isLoadingUpsellProducts,
  } = useUpsellProducts();
  const { orderData } = useContext(OrderDataContext);
  const configData = useContext(ConfigContext);

  useEffect(() => {
    if (!returnOrder) {
      navigate({
        to: "/",
        search: (prev) => prev,
      });
    }
  }, [returnOrder, navigate]);

  if (!returnOrder) return null;

  if (upsellProductsHasError) {
    throw new Error("An error ocurred");
  }

  const valueMultipler = orderData?.exchange_data?.upsell_value_multiplier ?? 1;

  const nextRoute = (upsell: boolean) => {
    if (upsell)
      return navigate({
        to: "/upsell-explore",
        search: (prev) => prev,
      });

    const atLeastOneNonExchange = returnOrder.line_items.some(
      (item) => item.exchange_variant_id === undefined
    );

    if (atLeastOneNonExchange && orderData?.gift_card_value_multiplier) {
      return navigate({
        to: "/refund-method",
        search: (prev) => prev,
      });
    }

    return navigate({
      to: "/confirm",
      search: (prev) => prev,
    });
  };

  return (
    <ReturnContainer>
      <div className="sm:min-w-1/2 max-w-2xl">
        <h1 className="font-semibold text-lg">
          {configData?.upsell_offer_header_text ?? "Shop"}
        </h1>
        <p className="text-sm mt-2">
          {configData?.upsell_offer_message ?? "Find something else"}
        </p>
        {isLoadingUpsellProducts ? (
          <LoadSpinner />
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 w-full items-center justify-center">
              {upsellProducts?.slice(0, 4).map((product) => (
                <div
                  className="flex hover:cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: "/upsell-explore",
                      search: (prev) => prev,
                    })
                  }
                >
                  <BoxedImage
                    src={product.image ?? product.variants[0].image}
                    alt={product.title}
                  />
                </div>
              ))}
            </div>

            <div className="grid sm:flex gap-6 w-full pt-12 sm:justify-center">
              <button
                className={cn(
                  "text-sm order-1 sm:order-0 w-full md:w-1/2 bg-white",
                  "rounded-sm font-normal border border-[rgb(235,235,235)]"
                )}
                onClick={() => nextRoute(false)}
              >
                {configData?.upsell_offer_no_thanks_text ??
                  "No thanks, proceed to review"}
              </button>
              <button
                className="order-0 sm:order-1 w-full sm:w-1/2 bg-black text-white rounded-sm text-sm"
                onClick={() => nextRoute(true)}
                style={{
                  backgroundColor:
                    configData?.button_color && configData.button_color !== ""
                      ? configData.button_color
                      : "black",
                }}
              >
                {(configData?.upsell_offer_yes_please_text ??
                  "Find something else") +
                  (valueMultipler > 1
                    ? ` (+${Math.round((valueMultipler - 1) * 100)}%)`
                    : "")}
              </button>
            </div>
          </>
        )}
      </div>
    </ReturnContainer>
  );
}
